import React from "react";
import { Play, Pause, SpeakerSlash } from "@phosphor-icons/react";
import { usePlayer } from "../context/PlayerContext";

function format(t) {
  if (!isFinite(t)) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

// Per-slot card audio controller — proxies to the global player so a single
// audio element drives the continuous-play queue.
export default function AudioPlayer({ src, rank }) {
  const { queue, currentIndex, isPlaying, currentTime, duration, play, pause, seek } = usePlayer();

  const isActive = currentIndex >= 0 && queue[currentIndex]?.rank === rank;
  const targetIndex = queue.findIndex((s) => s.rank === rank);
  const showTime = isActive ? currentTime : 0;
  const showDuration = isActive ? duration : 0;

  if (!src) {
    return (
      <div
        className="flex items-center gap-3 p-4 rounded-md bg-ink-800/60 border border-ink-600 text-zinc-500 text-sm font-mono"
        data-testid={`audio-empty-${rank}`}
      >
        <SpeakerSlash size={18} weight="bold" />
        No audio uploaded yet
      </div>
    );
  }

  const toggle = () => {
    if (isActive) {
      if (isPlaying) pause(); else play();
    } else if (targetIndex >= 0) {
      play(targetIndex);
    }
  };

  const onSeek = (e) => {
    if (!isActive) return;
    seek(Number(e.target.value));
  };

  return (
    <div
      className={`flex items-center gap-4 p-4 rounded-md bg-ink-800/60 border ${isActive ? "border-neon-cyan/50 shadow-[0_0_24px_rgba(255,138,60,0.18)]" : "border-ink-600"}`}
      data-testid={`audio-player-${rank}`}
    >
      <button
        onClick={toggle}
        aria-label={isActive && isPlaying ? "Pause" : "Play"}
        className="shrink-0 w-12 h-12 rounded-full bg-neon-cyan text-black flex items-center justify-center hover:bg-neon-cyanHover transition-colors duration-300 shadow-[0_0_24px_rgba(255,138,60,0.45)]"
        data-testid={`audio-toggle-${rank}`}
      >
        {isActive && isPlaying ? <Pause size={20} weight="fill" /> : <Play size={20} weight="fill" />}
      </button>

      <div className="flex-1 min-w-0">
        <input
          type="range"
          className="progress w-full"
          min={0}
          max={showDuration || 0}
          step={0.1}
          value={showTime}
          onChange={onSeek}
          disabled={!isActive}
          aria-label="Seek"
          data-testid={`audio-seek-${rank}`}
        />
        <div className="flex justify-between mt-1.5 font-mono text-[10px] text-zinc-500 tracking-wider">
          <span>{format(showTime)}</span>
          <span className={isActive ? "text-neon-cyan" : ""}>
            {isActive ? (isPlaying ? "● live" : "paused") : format(showDuration)}
          </span>
        </div>
      </div>
    </div>
  );
}
